import React from "react";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
export const US_ZONES = [
  ["America/New_York", "Eastern"], ["America/Chicago", "Central"], ["America/Denver", "Mountain"],
  ["America/Phoenix", "Arizona"], ["America/Los_Angeles", "Pacific"], ["America/Anchorage", "Alaska"],
  ["Pacific/Honolulu", "Hawaii"],
];
const week = (isOpen, start = "09:00", end = "17:00") =>
  Object.fromEntries(DAYS.map(([k]) => [k, { open: isOpen(k), start, end }]));
const weekdays = (k) => !["sat", "sun"].includes(k);
const PRESETS = [
  ["Mon–Fri 9–5", () => week(weekdays)],
  ["Mon–Fri 8–6", () => week(weekdays, "08:00", "18:00")],
  ["Every day 8–8", () => week(() => true, "08:00", "20:00")],
];
const SEL = "border border-black px-2 py-1.5 text-xs bg-white";
const pill = (on) => `border-2 border-black px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors duration-100 ${on ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`;

function DayRow({ k, label, row, setDay }) {
  const allDay = row.start === "00:00" && row.end === "24:00";
  return (
    <div className="flex flex-wrap items-center gap-2 py-1" data-testid={`hours-day-${k}`}>
      <label className="inline-flex items-center gap-2 w-24">
        <input type="checkbox" checked={row.open} onChange={(e) => setDay(k, { open: e.target.checked })} data-testid={`hours-day-${k}-open`} />
        <span className="text-xs font-bold">{label}</span>
      </label>
      {row.open ? (
        <>
          <label className="inline-flex items-center gap-1 text-[11px]">
            <input type="checkbox" checked={allDay} data-testid={`hours-day-${k}-allday`}
                   onChange={(e) => setDay(k, e.target.checked ? { start: "00:00", end: "24:00" } : { start: "09:00", end: "17:00" })} />
            All day
          </label>
          {!allDay && (
            <>
              <input type="time" className={SEL} value={row.start} onChange={(e) => setDay(k, { start: e.target.value })} data-testid={`hours-day-${k}-start`} />
              <span className="text-xs">to</span>
              <input type="time" className={SEL} value={row.end === "24:00" ? "23:59" : row.end} onChange={(e) => setDay(k, { end: e.target.value })} data-testid={`hours-day-${k}-end`} />
            </>
          )}
        </>
      ) : <span className="text-[11px] text-zinc-500">Closed</span>}
    </div>
  );
}

// Buyer lead hours: 24/7 by default, or set days and times. Saved as intake_criteria.accept_schedule.
export function AcceptHours({ value, onChange }) {
  const s = value || { mode: "always" };
  const days = s.days || PRESETS[0][1]();
  const put = (patch) => onChange({ after_hours: "other_buyer", timezone: null, ...s, days, ...patch });
  const setDay = (k, patch) => put({ days: { ...days, [k]: { ...days[k], ...patch } } });
  const set = s.mode === "set";
  return (
    <div className="space-y-3" data-testid="accept-hours">
      <div className="flex flex-wrap gap-2">
        <button type="button" className={pill(!set)} onClick={() => put({ mode: "always" })} data-testid="hours-mode-always">24/7 (every day, all day)</button>
        <button type="button" className={pill(set)} onClick={() => put({ mode: "set" })} data-testid="hours-mode-set">Set days and hours</button>
      </div>
      {!set ? (
        <div className="text-[11px] text-zinc-600" data-testid="hours-summary">Takes leads any time, every day.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map(([label, make], i) => (
              <button key={label} type="button" onClick={() => put({ days: make() })} data-testid={`hours-preset-${i}`}
                      className="border border-black px-2 py-1 text-[11px] hover:bg-black hover:text-white transition-colors duration-100">{label}</button>
            ))}
          </div>
          <div className="border-2 border-black bg-white px-3 py-1 divide-y divide-zinc-100">
            {DAYS.map(([k, label]) => <DayRow key={k} k={k} label={label} row={days[k]} setDay={setDay} />)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs">
              <div className="label-mono">Time zone</div>
              <select className={`${SEL} w-full mt-1`} value={s.timezone || ""} onChange={(e) => put({ timezone: e.target.value || null })} data-testid="hours-timezone">
                <option value="">Platform default (set in Platform settings)</option>
                {US_ZONES.map(([z, n]) => <option key={z} value={z}>{n}</option>)}
              </select>
            </label>
            <label className="text-xs">
              <div className="label-mono">Lead comes in when they&apos;re closed</div>
              <select className={`${SEL} w-full mt-1`} value={s.after_hours || "other_buyer"} onChange={(e) => put({ after_hours: e.target.value })} data-testid="hours-after">
                <option value="other_buyer">Send to another open buyer; if none, hold for them</option>
                <option value="hold">Hold it for this buyer until they open</option>
              </select>
            </label>
          </div>
          <div className="text-[11px] text-zinc-600">Leads from this buyer&apos;s own pages always wait for them, since the visitor asked for this firm.</div>
        </>
      )}
    </div>
  );
}
